import { createFileRoute } from '@tanstack/react-router'
import { LEGAL_CONFIG, LEGAL_VERSIONS } from '@/legal.config'
import { LegalVersionMeta } from '@/routes/legal/-legal-version-meta'

export const Route = createFileRoute('/legal/_layout/subprocessors')({
  component: SubprocessorsPage,
})

type Subprocessor = {
  readonly name: string
  readonly purpose: string
  readonly location: string
  readonly transfer: string
}

const SUBPROCESSORS: readonly Subprocessor[] = [
  {
    name: 'Convex, Inc.',
    purpose: 'Database and backend compute',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'Cloudflare, Inc.',
    purpose:
      'CDN, hosting (Pages), object storage (R2), bot protection (Turnstile)',
    location: 'USA/EU',
    transfer: 'SCCs / adequacy',
  },
  {
    name: 'OpenAI, Inc.',
    purpose: 'AI competency analysis of interview transcripts',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'Anthropic, PBC',
    purpose: 'AI-assisted chat and supplementary analysis',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'LiveKit, Inc.',
    purpose: 'Real-time video and audio transport',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'Resend, Inc.',
    purpose: 'Transactional email delivery',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'Polar Software, Inc.',
    purpose: 'Billing and payment processing',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'PostHog, Inc.',
    purpose: 'Product analytics',
    location: 'USA/EU',
    transfer: 'SCCs',
  },
  {
    name: 'Sentry (Functional Software, Inc.)',
    purpose: 'Error monitoring',
    location: 'USA',
    transfer: 'SCCs',
  },
  {
    name: 'Google LLC',
    purpose: 'Tag management (Google Tag Manager)',
    location: 'USA',
    transfer: 'SCCs',
  },
]

function SubprocessorsPage() {
  return (
    <article className="prose prose-neutral dark:prose-invert max-w-none">
      <h1>Sub-processor List</h1>
      <LegalVersionMeta
        current={LEGAL_VERSIONS.subprocessors.current}
        history={LEGAL_VERSIONS.subprocessors.history}
      />

      <p>
        {LEGAL_CONFIG.companyName} engages the following third-party
        sub-processors to deliver the {LEGAL_CONFIG.productName} service.
        Customers are notified of any additions or replacements at least 14 days
        in advance, as set out in the{' '}
        <a href="/legal/dpa">Data Processing Agreement</a>.
      </p>
      <p>
        SCCs = Standard Contractual Clauses (Commission Decision 2021/914/EU),
        the approved mechanism for personal data transfers from the EEA to third
        countries.
      </p>

      <table>
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Purpose</th>
            <th>Location</th>
            <th>Transfer mechanism</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((sp) => (
            <tr key={sp.name}>
              <td>{sp.name}</td>
              <td>{sp.purpose}</td>
              <td>{sp.location}</td>
              <td>{sp.transfer}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Questions</h2>
      <p>
        To object to a sub-processor change or ask questions about this list,
        contact{' '}
        <a href={`mailto:${LEGAL_CONFIG.privacyEmail}`}>
          {LEGAL_CONFIG.privacyEmail}
        </a>
        .
      </p>
    </article>
  )
}
